'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { useLocale } from 'next-intl';
import { X } from '@phosphor-icons/react';
import { useCurrency } from '@/lib/currency/CurrencyContext';
import { formatPrice } from '@/lib/currency/formatPrice';
import { useListingTransition } from '@/components/storefront/ListingTransitionContext';
import { toggleVariantOption, type VariantOptionSelection, writeVariantOptions } from '@/lib/storefront/products/listFilterParams';
import type { FacetsDTO } from '@/lib/storefront/products/listService';
type AppliedFilters = {
  brandSlugs: string[];
  colors: string[]; // English values
  tagSlugs: string[];
  categorySlugs: string[];
  variantOptions: VariantOptionSelection;
  minPrice: number | null;
  maxPrice: number | null;
  inStockOnly: boolean;
};
type Chip = {
  key: string;
  label: string;
  remove: (params: URLSearchParams) => void;
};
export default function ActiveFilterChips(t0) {
  const $ = _c(30);
  const {
    facets,
    applied
  } = t0;
  const locale = useLocale();
  const isRTL = locale === "ar";
  const {
    navigateWithParams
  } = useListingTransition();
  const {
    currency
  } = useCurrency();
  let out;
  if ($[0] !== applied.brandSlugs || $[1] !== applied.categorySlugs || $[2] !== applied.colors || $[3] !== applied.inStockOnly || $[4] !== applied.maxPrice || $[5] !== applied.minPrice || $[6] !== applied.tagSlugs || $[7] !== applied.variantOptions || $[8] !== currency || $[9] !== facets || $[10] !== isRTL) {
    out = [];
    for (const slug of applied.brandSlugs) {
      const brand = facets.brands.find(b => b.slug === slug);
      if (!brand) {
        continue;
      }
      out.push({
        key: `brand:${slug}`,
        label: brand.name,
        remove: p => {
          const next = applied.brandSlugs.filter(b_0 => b_0 !== slug);
          if (next.length === 0) {
            p.delete("brand");
          } else {
            p.set("brand", next.join(","));
          }
        }
      });
    }
    for (const valueEn of applied.colors) {
      const color = facets.colors.find(c => (c.value_en || c.value_ar) === valueEn);
      const label = color ? isRTL ? color.value_ar : color.value_en : valueEn;
      out.push({
        key: `color:${valueEn}`,
        label,
        remove: p_0 => {
          const next_0 = applied.colors.filter(c_0 => c_0 !== valueEn);
          if (next_0.length === 0) {
            p_0.delete("color");
          } else {
            p_0.set("color", next_0.join(","));
          }
        }
      });
    }
    for (const slug_0 of applied.categorySlugs) {
      const category = facets.categories.find(c_1 => c_1.slug === slug_0);
      if (!category) {
        continue;
      }
      out.push({
        key: `category:${slug_0}`,
        label: category.name,
        remove: p_1 => {
          const next_1 = applied.categorySlugs.filter(c_2 => c_2 !== slug_0);
          if (next_1.length === 0) {
            p_1.delete("category");
          } else {
            p_1.set("category", next_1.join(","));
          }
        }
      });
    }
    for (const [group, values] of Object.entries(applied.variantOptions)) {
      const facetGroup = facets.optionGroups.find(g => g.key === group);
      for (const value of values) {
        const label_0 = facetGroup?.values.find(v => v.value === value)?.label ?? value;
        out.push({
          key: `opt:${group}:${value}`,
          label: label_0,
          remove: p_2 => writeVariantOptions(p_2, toggleVariantOption(applied.variantOptions, group, value))
        });
      }
    }
    for (const slug_1 of applied.tagSlugs) {
      const tag = facets.tags.find(t => t.slug === slug_1);
      if (!tag) {
        continue;
      }
      out.push({
        key: `tag:${slug_1}`,
        label: tag.name,
        remove: p_3 => {
          const next_2 = applied.tagSlugs.filter(t_0 => t_0 !== slug_1);
          if (next_2.length === 0) {
            p_3.delete("tag");
          } else {
            p_3.set("tag", next_2.join(","));
          }
        }
      });
    }
    if (applied.minPrice !== null || applied.maxPrice !== null) {
      let t1;
      if ($[12] !== applied.minPrice || $[13] !== currency) {
        t1 = applied.minPrice !== null ? formatPrice(applied.minPrice, currency) : "";
        $[12] = applied.minPrice;
        $[13] = currency;
        $[14] = t1;
      } else {
        t1 = $[14];
      }
      const minLabel = t1;
      let t2;
      if ($[15] !== applied.maxPrice || $[16] !== currency) {
        t2 = applied.maxPrice !== null ? formatPrice(applied.maxPrice, currency) : "";
        $[15] = applied.maxPrice;
        $[16] = currency;
        $[17] = t2;
      } else {
        t2 = $[17];
      }
      const maxLabel = t2;
      let label_1;
      if (applied.minPrice !== null && applied.maxPrice !== null) {
        label_1 = `${minLabel} – ${maxLabel}`;
      } else {
        if (applied.minPrice !== null) {
          label_1 = isRTL ? `من ${minLabel}` : `From ${minLabel}`;
        } else {
          label_1 = isRTL ? `حتى ${maxLabel}` : `Up to ${maxLabel}`;
        }
      }
      let t3;
      if ($[18] !== label_1) {
        t3 = {
          key: "price",
          label: label_1,
          remove: _temp
        };
        $[18] = label_1;
        $[19] = t3;
      } else {
        t3 = $[19];
      }
      out.push(t3);
    }
    if (applied.inStockOnly) {
      const t1 = isRTL ? "\u0645\u062A\u0648\u0641\u0631 \u0641\u0642\u0637" : "In stock only";
      let t2;
      if ($[20] !== t1) {
        t2 = {
          key: "in_stock",
          label: t1,
          remove: _temp2
        };
        $[20] = t1;
        $[21] = t2;
      } else {
        t2 = $[21];
      }
      out.push(t2);
    }
    $[0] = applied.brandSlugs;
    $[1] = applied.categorySlugs;
    $[2] = applied.colors;
    $[3] = applied.inStockOnly;
    $[4] = applied.maxPrice;
    $[5] = applied.minPrice;
    $[6] = applied.tagSlugs;
    $[7] = applied.variantOptions;
    $[8] = currency;
    $[9] = facets;
    $[10] = isRTL;
    $[11] = out;
  } else {
    out = $[11];
  }
  const chips = out;
  let t1;
  if ($[22] !== navigateWithParams) {
    t1 = chip => {
      navigateWithParams(params => {
        chip.remove(params);
        params.delete("page");
      });
    };
    $[22] = navigateWithParams;
    $[23] = t1;
  } else {
    t1 = $[23];
  }
  const removeChip = t1;
  if (chips.length === 0) {
    return null;
  }
  const t2 = isRTL ? "\u0627\u0644\u0641\u0644\u0627\u062A\u0631 \u0627\u0644\u0645\u0637\u0628\u0642\u0629" : "Applied filters";
  let t3;
  if ($[24] !== chips || $[25] !== removeChip) {
    t3 = chips.map(chip_0 => <button key={chip_0.key} type="button" onClick={() => removeChip(chip_0)} className="tap-target inline-flex items-center gap-1.5 h-7 ps-3 pe-2 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15 transition-colors"><span>{chip_0.label}</span><X size={12} weight="bold" /></button>);
    $[24] = chips;
    $[25] = removeChip;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  let t4;
  if ($[27] !== t2 || $[28] !== t3) {
    t4 = <div className="tap-grid mt-3 flex items-center gap-2 flex-wrap" role="region" aria-label={t2}>{t3}</div>;
    $[27] = t2;
    $[28] = t3;
    $[29] = t4;
  } else {
    t4 = $[29];
  }
  return t4;
}
function _temp2(p_5) {
  return p_5.delete("in_stock");
}
function _temp(p_4) {
  p_4.delete("min_price");
  p_4.delete("max_price");
}